/**
 * DateNav Component
 * Collapsible date navigation with scrollable date picker
 * Created: 2025-12-23 17:45 EST
 */

import { useState, useRef, useEffect } from 'react'
import './DateNav.css'

// Ticker messages - date/schedule specific
const TICKER_MESSAGES = [
  'GAME SCHEDULES',
  'LIVE NOW',
  'UPCOMING',
  'FINAL SCORES',
  'PREGAME LINES',
  'RESULTS'
]

function DateNav({
  selectedDate,
  onDateSelect,
  collapsed,
  onToggleCollapse
}) {
  const [showCalendar, setShowCalendar] = useState(false)
  const dateScrollRef = useRef(null)

  // Generate dates for the scrollable date picker (14 days before and after today)
  const generateDateRange = () => {
    const dates = []
    const today = new Date()
    for (let i = -14; i <= 14; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const dateRange = generateDateRange()

  // Format date label for the toggle button
  const formatDateLabel = (date) => {
    if (!date) return 'Select Date'

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)

    const diffDays = Math.round((compareDate - today) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  // Check if two dates are the same day
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate()
  }

  // Scroll dates left or right
  const scrollDates = (direction) => {
    if (dateScrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200
      dateScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  // Scroll to a specific date
  const scrollToDate = (targetDate) => {
    if (!dateScrollRef.current || !targetDate) return
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const target = new Date(targetDate)
    target.setHours(0, 0, 0, 0)
    const daysDiff = Math.round((target - today) / (1000 * 60 * 60 * 24))

    // Each date button is ~58px wide (52px + 6px gap)
    const dateButtonWidth = 58
    const targetIndex = daysDiff + 14
    const scrollPosition = targetIndex * dateButtonWidth - (dateScrollRef.current.clientWidth / 2) + (dateButtonWidth / 2)
    dateScrollRef.current.scrollTo({ left: Math.max(0, scrollPosition), behavior: 'smooth' })
  }

  // Center on today when first expanded
  useEffect(() => {
    if (!collapsed && dateScrollRef.current) {
      setTimeout(() => scrollToDate(selectedDate || new Date()), 100)
    }
  }, [collapsed])

  // Handle today button click
  const goToToday = () => {
    const today = new Date()
    onDateSelect(today)
    setTimeout(() => scrollToDate(today), 50)
  }

  // Handle calendar date selection
  const handleCalendarChange = (e) => {
    const newDate = new Date(e.target.value)
    onDateSelect(newDate)
    setShowCalendar(false)
    setTimeout(() => scrollToDate(newDate), 50)
  }

  return (
    <div className={`date-nav-section ${collapsed ? 'collapsed' : ''}`}>
      {/* Collapse Toggle */}
      <button
        className="date-nav-toggle"
        onClick={onToggleCollapse}
        title={collapsed ? 'Expand Dates' : 'Collapse Dates'}
      >
        <span className="toggle-label">{formatDateLabel(selectedDate)}</span>

        {/* Ticker - scrolls normal direction */}
        <div className="date-ticker-container">
          <div className="date-ticker-scroll">
            {TICKER_MESSAGES.map((msg, i) => (
              <span key={i}>
                {msg}
                <span className="separator-date">•</span>
              </span>
            ))}
            {/* Duplicate for seamless loop */}
            {TICKER_MESSAGES.map((msg, i) => (
              <span key={`dup-${i}`}>
                {msg}
                <span className="separator-date">•</span>
              </span>
            ))}
          </div>
        </div>

        <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'}`}></i>
      </button>

      {/* Date Navigation */}
      {!collapsed && (
        <div className="date-filter-row">
          {/* Left scroll button */}
          <button className="date-scroll-btn" onClick={() => scrollDates('left')}>
            <i className="fas fa-chevron-left"></i>
          </button>

          {/* Scrollable date buttons */}
          <div className="date-scroll-container" ref={dateScrollRef}>
            {dateRange.map((date, idx) => (
              <button
                key={idx}
                className={`date-btn ${isSameDay(date, selectedDate) ? 'active' : ''} ${isSameDay(date, new Date()) ? 'today' : ''}`}
                onClick={() => onDateSelect(date)}
              >
                <span className="date-day">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                <span className="date-num">{date.getDate()}</span>
              </button>
            ))}
          </div>

          {/* Right scroll button */}
          <button className="date-scroll-btn" onClick={() => scrollDates('right')}>
            <i className="fas fa-chevron-right"></i>
          </button>

          {/* Action buttons */}
          <div className="date-actions">
            <button
              className="date-action-btn today-btn"
              onClick={goToToday}
              title="Go to Today"
            >
              <i className="fas fa-dot-circle"></i>
            </button>

            <button
              className="date-action-btn calendar-btn"
              onClick={() => setShowCalendar(!showCalendar)}
              title="Open Calendar"
            >
              <i className="fas fa-calendar-alt"></i>
            </button>
          </div>

          {/* Calendar Popup */}
          {showCalendar && (
            <div className="calendar-popup">
              <input
                type="date"
                value={selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                onChange={handleCalendarChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DateNav
